import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useLenis } from "../components/motion/Reveal";
import { BlogHeader } from "../components/site/BlogHeader";
import { Footer } from "../components/site/Footer";
import { api } from "../lib/api";

export default function Blog() {
  useLenis();
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get(`/blog?lang=${lang}`).then((r) => setPosts(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [lang]);

  const categories = useMemo(
    () => ["all", ...Array.from(new Set(posts.map((p) => p.category)))],
    [posts]
  );
  const filtered = cat === "all" ? posts : posts.filter((p) => p.category === cat);

  return (
    <div className="grain relative bg-charcoal min-h-screen overflow-x-hidden">
      <BlogHeader backLabel={t.blog.all} />

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-10">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-emerald2" />
            <span className="text-emerald2 text-xs uppercase tracking-[0.3em] font-medium">{t.blog.kicker}</span>
          </div>
          <h1 className="font-display font-extrabold tracking-tighter text-5xl md:text-7xl leading-[0.9]">{t.blog.title}</h1>
          <p className="mt-5 text-white/60 max-w-xl">{t.blog.sub}</p>
        </motion.div>

        {categories.length > 1 && (
          <div className="mt-10 flex flex-wrap gap-2" data-testid="blog-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`blog-filter-${c}`}
                className={`text-xs uppercase tracking-[0.15em] rounded-full px-4 py-2 border transition-colors ${
                  cat === c ? "border-lime2 text-lime2 bg-lime2/10" : "border-white/15 text-white/50 hover:border-white/30"
                }`}
              >
                {c === "all" ? t.blog.all : c}
              </button>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <p className="mt-16 text-white/40" data-testid="blog-empty">{t.blog.empty}</p>
        ) : (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link to={`/blog/${p.slug}`} data-testid={`blog-item-${p.slug}`} className="group block">
                  <motion.div whileHover="hover" className="rounded-2xl overflow-hidden border border-white/10 bg-[#181818] h-full">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <motion.img src={p.cover} alt={p.title} variants={{ hover: { scale: 1.07 } }} transition={{ duration: 0.6 }} className="w-full h-full object-cover" />
                      <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] bg-black/60 backdrop-blur-md border border-white/20 rounded-full px-3 py-1 text-lime2">
                        {p.category}
                      </span>
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-xl font-bold leading-snug group-hover:text-lime2 transition-colors">{p.title}</h3>
                      <p className="text-white/55 text-sm mt-3 line-clamp-3">{p.excerpt}</p>
                      <div className="mt-5 flex items-center justify-between text-xs text-white/40">
                        <span>{p.author} · {p.read_time} {t.blog.readTime}</span>
                        <ArrowUpRight size={16} className="group-hover:text-lime2 transition-colors" />
                      </div>
                    </div>
                  </motion.div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
